import React, { useEffect, useState } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn } from '../../lib/utils';
import { useTableOrder } from '../../lib/table-context';
import { isPreorderRestaurant, DELIVERY_TIME_SLOTS, getUpcomingSaturdays, formatDeliveryDate, toDateString, getOrderingWindowMessage } from '../../lib/preorder';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
  preorder_date?: string;
  preorder_time_slot?: string;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading
}: CheckoutFormProps) {
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    delivery_type: isTableOrder ? 'dine_in' : 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: isTableOrder ? tableOrder?.tableId || '' : '',
    notes: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData, string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  const isPreorder = isPreorderRestaurant(restaurant?.id);
  const preorderSaturdays = isPreorder ? getUpcomingSaturdays(3) : [];
  const preorderWindow = isPreorder ? getOrderingWindowMessage() : null;
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData, string>> = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'El nombre es requerido';
    // Phone is optional for QR table orders, required otherwise
    if (!isTableOrder) {
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
        newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
      }
    } else if (formData.customer_phone.trim() && formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
    }
    if (formData.delivery_type === 'dine_in' && !isTableOrder) {
      if (!formData.table_id.trim()) newErrors.table_id = 'Indica tu número de mesa';
    }
    if (isPreorder) {
      if (!formData.preorder_date) (newErrors as any).preorder_date = 'Selecciona una fecha de entrega';
      if (!formData.preorder_time_slot) (newErrors as any).preorder_time_slot = 'Selecciona un horario de entrega';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-392">
      {/* QR Table Banner */}
      {isTableOrder && tableOrder && <section className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200 flex items-center gap-4" data-id="element-393">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0" data-id="element-394">
            <Utensils className="w-6 h-6 text-emerald-600" data-id="element-395" />
          </div>
          <div data-id="element-396">
            <p className="font-bold text-emerald-900 text-lg" data-id="element-397">
              {tableOrder.tableName} · Autoatención
            </p>
            <p className="text-sm text-emerald-700" data-id="element-398">
              Tu pedido se enviará directamente a esta mesa
            </p>
          </div>
        </section>}

      {/* Contact Info */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-399">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-400">Tus Datos</h3>
        <div className="space-y-4" data-id="element-401">
          <Input label="Nombre completo" placeholder="Ej. Juan Pérez" value={formData.customer_name} onChange={e => handleChange('customer_name', e.target.value)} error={errors.customer_name} required data-id="element-402" />
          <Input label={isTableOrder ? 'Teléfono (opcional)' : 'Teléfono (WhatsApp)'} placeholder="Ej. 8888 8888" type="tel" value={formData.customer_phone} onChange={e => handleChange('customer_phone', e.target.value)} error={errors.customer_phone} required={!isTableOrder} data-id="element-403" />
        </div>
      </section>

      {/* Preorder Schedule — for preorder restaurants */}
      {isPreorder && preorderWindow && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-404">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-405">
            📦 Entrega Programada
          </h3>

          {/* Schedule Info */}
          <div className={cn('rounded-xl p-4 border mb-5', preorderWindow.isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')} data-id="element-406">
            <p className={cn('font-bold text-sm mb-1', preorderWindow.isOpen ? 'text-emerald-900' : 'text-red-900')} data-id="element-407">
              {preorderWindow.title}
            </p>
            <p className={cn('text-xs', preorderWindow.isOpen ? 'text-emerald-700' : 'text-red-700')} data-id="element-408">
              {preorderWindow.message}
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500" data-id="element-409">
              <span data-id="element-410">📅 Pedidos: Lun–Vie</span>
              <span data-id="element-411">·</span>
              <span data-id="element-412">🚚 Entregas: Sábado 8am–4pm</span>
            </div>
          </div>

          {/* Date Selection */}
          <div className="mb-5" data-id="element-413">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-414">
              Fecha de entrega (sábado)
            </label>
            <div className="space-y-2" data-id="element-415">
              {preorderSaturdays.map(sat => {
            const dateStr = toDateString(sat);
            const isSelected = formData.preorder_date === dateStr;
            return <button key={dateStr} type="button" onClick={() => handleChange('preorder_date' as any, dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-416">
                    📅 {formatDeliveryDate(sat)}
                  </button>;
          })}
            </div>
            {(errors as any).preorder_date && <p className="text-sm text-red-500 mt-1" data-id="element-417">
                {(errors as any).preorder_date}
              </p>}
          </div>

          {/* Time Slot Selection */}
          {formData.preorder_date && <div data-id="element-418">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-419">
                Horario de entrega
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-420">
                {DELIVERY_TIME_SLOTS.map(slot => {
            const isSelected = formData.preorder_time_slot === slot.id;
            return <button key={slot.id} type="button" onClick={() => handleChange('preorder_time_slot' as any, slot.id)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-421">
                      🕐 {slot.label}
                    </button>;
          })}
              </div>
              {(errors as any).preorder_time_slot && <p className="text-sm text-red-500 mt-1" data-id="element-422">
                  {(errors as any).preorder_time_slot}
                </p>}
            </div>}
        </section>}

      {/* Delivery Type — hidden for QR table orders */}
      {!isTableOrder && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-423">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-424">
            {isPreorder ? 'Método de Recogida' : 'Método de Entrega'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-425">
            {settings?.accept_delivery && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'delivery' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-426">
                <input type="radio" name="delivery_type" value="delivery" checked={formData.delivery_type === 'delivery'} onChange={() => handleChange('delivery_type', 'delivery')} className="sr-only" data-id="element-427" />
                <MapPin className="w-6 h-6 mb-2" data-id="element-428" />
                <span className="font-semibold" data-id="element-429">Delivery</span>
              </label>}

            {settings?.accept_pickup && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'pickup' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-430">
                <input type="radio" name="delivery_type" value="pickup" checked={formData.delivery_type === 'pickup'} onChange={() => handleChange('delivery_type', 'pickup')} className="sr-only" data-id="element-431" />
                <Store className="w-6 h-6 mb-2" data-id="element-432" />
                <span className="font-semibold" data-id="element-433">Para llevar</span>
              </label>}

            {!isPreorder && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-434">
                <input type="radio" name="delivery_type" value="dine_in" checked={formData.delivery_type === 'dine_in'} onChange={() => handleChange('delivery_type', 'dine_in')} className="sr-only" data-id="element-435" />
                <Utensils className="w-6 h-6 mb-2" data-id="element-436" />
                <span className="font-semibold" data-id="element-437">Comer aquí</span>
              </label>}
          </div>

          {/* Conditional Fields based on Delivery Type */}
          <div className="mt-6 space-y-4" data-id="element-438">
            {formData.delivery_type === 'delivery' && <>
                <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-439" />

                <div className="space-y-2" data-id="element-440">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-441">
                    Ubicación GPS (Opcional pero recomendado)
                  </label>

                  <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-442">
                    {geoStatus === 'idle' && <>
                        <Navigation className="w-5 h-5 mr-2" data-id="element-443" />
                        Usar mi ubicación actual
                      </>}
                    {geoStatus === 'loading' && <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-444" />
                        Obteniendo ubicación...
                      </>}
                    {geoStatus === 'success' && <>
                        <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-445" />
                        Ubicación capturada
                      </>}
                    {geoStatus === 'error' && <>
                        <AlertCircle className="w-5 h-5 mr-2" data-id="element-446" />
                        Reintentar ubicación
                      </>}
                  </button>

                  {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-447">
                      <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-448">
                        📍 Ver en el mapa
                      </a>
                      <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-449">
                        Actualizar
                      </button>
                    </div>}

                  {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-450">{geoError}</p>}
                </div>
              </>}

            {formData.delivery_type === 'dine_in' && <Input label="Número o nombre de mesa" placeholder="Ej. Mesa 4, Barra, etc." value={formData.table_id} onChange={e => handleChange('table_id', e.target.value)} error={errors.table_id} required data-id="element-451" />}
          </div>
        </section>}

      {/* Payment Method */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-452">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-453">Método de Pago</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-454">
          {showSinpe && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'sinpe' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-455">
              <input type="radio" name="payment_method" value="sinpe" checked={formData.payment_method === 'sinpe'} onChange={() => handleChange('payment_method', 'sinpe')} className="sr-only" data-id="element-456" />
              <Smartphone className="w-5 h-5 mr-3" data-id="element-457" />
              <span className="font-semibold" data-id="element-458">SINPE Móvil</span>
            </label>}

          {showCash && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'cash' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-459">
              <input type="radio" name="payment_method" value="cash" checked={formData.payment_method === 'cash'} onChange={() => handleChange('payment_method', 'cash')} className="sr-only" data-id="element-460" />
              <Banknote className="w-5 h-5 mr-3" data-id="element-461" />
              <span className="font-semibold" data-id="element-462">Efectivo</span>
            </label>}

          {showCard && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'card' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-463">
              <input type="radio" name="payment_method" value="card" checked={formData.payment_method === 'card'} onChange={() => handleChange('payment_method', 'card')} className="sr-only" data-id="element-464" />
              <CreditCard className="w-5 h-5 mr-3" data-id="element-465" />
              <span className="font-semibold" data-id="element-466">Tarjeta (Datáfono)</span>
            </label>}
        </div>
      </section>

      {/* Order Notes */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-467">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-468">
          Notas Adicionales
        </h3>
        <Textarea placeholder="¿Algo más que debamos saber sobre tu pedido o entrega?" value={formData.notes} onChange={e => handleChange('notes', e.target.value)} data-id="element-469" />
      </section>

      <Button type="submit" size="lg" fullWidth isLoading={isLoading} className="text-lg shadow-xl" data-id="element-470">
        {isPreorder ? 'Confirmar Preorden' : 'Confirmar Pedido'}
      </Button>
    </form>;
}